/** Mirrors `api/src/lib/permissions.ts` — the API is the real gate; keep keys and copy in sync. */
export const PERMISSION_GROUPS = [
	{
		id: "workspace",
		label: "Workspace",
		permissions: [
			{
				key: "workspace.manage",
				label: "Edit workspace",
				description: "Rename the workspace and change workspace settings.",
			},
			{
				key: "billing.manage",
				label: "Manage billing",
				description: "Change plan, buy credits and seats, view invoices.",
			},
		],
	},
	{
		id: "team",
		label: "Team",
		permissions: [
			{
				key: "members.invite",
				label: "Invite people",
				description: "Send, copy and revoke workspace invites.",
			},
			{
				key: "members.manage",
				label: "Manage members",
				description: "Change members' roles and remove people from the workspace.",
			},
			{
				key: "roles.manage",
				label: "Manage roles",
				description: "Create, edit and delete roles and their permissions.",
			},
		],
	},
	{
		id: "projects",
		label: "Projects",
		permissions: [
			{ key: "projects.create", label: "Create projects", description: "Start new projects." },
			{
				key: "projects.manage",
				label: "Edit projects",
				description: "Edit project settings and brand, archive and restore projects.",
			},
		],
	},
	{
		id: "flows",
		label: "Flows & artifacts",
		permissions: [
			{
				key: "flows.edit",
				label: "Build flows",
				description: "Create flows and edit them on the canvas.",
			},
			{
				key: "flows.run",
				label: "Run flows",
				description: "Run flows and schedules and cancel runs. Spends credits.",
			},
			{
				key: "schedules.manage",
				label: "Manage schedules",
				description: "Create, edit and pause recurring schedules.",
			},
			{
				key: "artifacts.manage",
				label: "Manage artifacts",
				description: "Upload, rename and delete images, videos and text.",
			},
		],
	},
	{
		id: "publishing",
		label: "Publishing",
		permissions: [
			{
				key: "posts.create",
				label: "Write posts",
				description: "Compose drafts, submit them for approval and edit unscheduled posts.",
			},
			{
				key: "posts.publish",
				label: "Approve & publish",
				description: "Approve, schedule, publish and unschedule posts.",
			},
			{
				key: "social.manage",
				label: "Manage channels",
				description: "Connect and disconnect social accounts and edit posting slots.",
			},
			{
				key: "comments.reply",
				label: "Reply to comments",
				description: "Reply to, hide and triage comments.",
			},
			{
				key: "ads.manage",
				label: "Manage ads",
				description: "Connect ad accounts, boost posts and control campaigns. Spends money.",
			},
		],
	},
	{
		id: "planning",
		label: "Planning & AI",
		permissions: [
			{
				key: "strategy.edit",
				label: "Edit strategy",
				description: "Edit the content strategy and refresh learnings.",
			},
			{
				key: "plans.edit",
				label: "Edit content plans",
				description: "Create and edit content plans and promote ideas to posts.",
			},
			{
				key: "competitors.manage",
				label: "Track competitors",
				description: "Add and remove competitors and run scrapes. Spends credits.",
			},
			{
				key: "assistant.use",
				label: "Use the assistant",
				description:
					"Chat with the assistant. Its actions still need the matching permissions above.",
			},
			{
				key: "agents.manage",
				label: "Manage agents",
				description:
					"Create, edit, pause and run autonomous agents. Runs spend credits; agents act with their creator's permissions.",
			},
		],
	},
] as const;

export type Permission = (typeof PERMISSION_GROUPS)[number]["permissions"][number]["key"];

export const PERMISSIONS: readonly Permission[] = PERMISSION_GROUPS.flatMap((group) =>
	group.permissions.map((p) => p.key),
);

export const PERMISSION_LABELS = new Map<string, string>(
	PERMISSION_GROUPS.flatMap((group) => group.permissions.map((p) => [p.key, p.label])),
);

export const normalizePermissions = (values: readonly string[]): Permission[] =>
	PERMISSIONS.filter((p) => values.includes(p));
